/**
 * ClipRecordingIndicator - Recording UI for Personal Context Clips
 *
 * Displays during clip recording:
 * - Muted label (when meeting mic is muted)
 * - Countdown timer
 * - Stop button
 *
 * Design follows DESIGN_SYSTEM.md:
 * - Calm, professional tone
 * - No emojis
 * - Subtle, non-intrusive
 *
 * @see useAudioClip.js for recording logic
 */

import { memo } from 'react';

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * ClipRecordingIndicator Component
 *
 * @param {Object} props
 * @param {boolean} props.isRecording - Whether currently recording
 * @param {number} props.recordingTime - Current recording time in seconds
 * @param {number} props.maxDuration - Maximum forward recording duration in seconds
 * @param {boolean} props.isMeetingMuted - Whether meeting mic is muted
 * @param {Function} props.onStop - Callback to stop recording
 */
function ClipRecordingIndicator({
  isRecording,
  recordingTime,
  maxDuration,
  isMeetingMuted,
  onStop
}) {
  if (!isRecording) return null;

  const remainingTime = Math.max(0, maxDuration - recordingTime);
  const progress = (recordingTime / maxDuration) * 100;

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[70] animate-fade-in">
      <div className="bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-4">
        {/* Recording indicator pulse */}
        <div className="relative flex-shrink-0">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75" />
        </div>

        {/* Muted label */}
        {isMeetingMuted && (
          <div className="flex items-center gap-1.5 text-[#8e8e93] text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
            <span>Muted</span>
            <span className="text-white/30">|</span>
          </div>
        )}

        {/* Recording context note label */}
        <span className="text-white/70 text-sm">Recording context note</span>

        {/* Timer with progress */}
        <div className="flex items-center gap-2">
          <div className="relative w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-200"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <span className="text-white text-sm font-mono tabular-nums min-w-[4rem]">
            {formatTime(recordingTime)} / {formatTime(maxDuration)}
          </span>
        </div>

        {/* Stop button */}
        <button
          onClick={onStop}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          Stop
        </button>
      </div>

      {/* Subtle hint */}
      <p className="text-center text-[#8e8e93] text-xs mt-2">
        Recording your microphone only
      </p>
    </div>
  );
}

/**
 * AddClipButton - Button to start recording a context clip
 *
 * @param {Object} props
 * @param {boolean} props.isBuffering - Whether buffer is active
 * @param {boolean} props.hasPermission - Whether mic permission is granted
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onClick - Callback when clicked
 */
export function AddClipButton({
  isBuffering,
  hasPermission,
  disabled,
  onClick
}) {
  // Hide if no mic permission
  if (hasPermission === false) return null;

  return (
    <button
      onClick={onClick}
      disabled={disabled || !isBuffering}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
        disabled || !isBuffering
          ? 'bg-white/5 text-white/30 cursor-not-allowed'
          : 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-700/50'
      }`}
      title={!isBuffering ? 'Context buffer not active' : 'Add audio context clip'}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
      Add context clip
    </button>
  );
}

/**
 * ClipAttachment - Display for attached audio clip on a note
 *
 * @param {Object} props
 * @param {Object} props.audioClip - Audio clip metadata {url, duration, size}
 * @param {Function} props.onPlay - Callback to play the clip
 */
export function ClipAttachment({ audioClip, onPlay }) {
  if (!audioClip?.url) return null;

  const durationDisplay = audioClip.duration
    ? `${audioClip.duration}s`
    : 'Audio clip';

  return (
    <div className="mt-3 p-2 bg-blue-900/30 border border-blue-700/50 rounded-lg flex items-center gap-2">
      <button
        onClick={onPlay}
        className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-500 rounded-full transition-colors"
      >
        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-blue-400 text-sm font-medium">Context clip</p>
        <p className="text-blue-400/60 text-xs">{durationDisplay}</p>
      </div>
      <svg className="w-4 h-4 text-blue-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    </div>
  );
}

export default memo(ClipRecordingIndicator);
